if (!customElements.get('ks-functional-header')) {
  class KSFunctionalHeader extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.searchTimer = null;
    }

    connectedCallback() {
      this.input = this.querySelector('[data-predictive-input]');
      this.results = this.querySelector('[data-predictive-results]');
      this.drawer = this.querySelector('[data-mobile-drawer]');
      this.toggle = this.querySelector('[data-mobile-toggle]');
      this.closeButtons = this.querySelectorAll('[data-mobile-close]');
      this.cartCount = this.querySelector('[data-cart-count]');

      this.input?.addEventListener('input', () => {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.search(this.input.value), 180);
      });
      this.input?.addEventListener('focus', () => {
        if (this.results?.innerHTML.trim()) this.openResults();
      });
      this.input?.addEventListener('keydown', (event) => this.onSearchKeydown(event));
      this.toggle?.addEventListener('click', () => this.openDrawer());
      this.closeButtons.forEach((button) => button.addEventListener('click', () => this.closeDrawer()));
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onDocumentKeydown);
      document.addEventListener('cart:refresh', this.refreshCart);
      document.addEventListener('cart:updated', this.refreshCart);
      window.addEventListener('pageshow', this.refreshCart);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      document.removeEventListener('cart:refresh', this.refreshCart);
      document.removeEventListener('cart:updated', this.refreshCart);
      window.removeEventListener('pageshow', this.refreshCart);
      this.abortController?.abort();
    }

    onDocumentClick = (event) => {
      if (!this.contains(event.target)) this.closeResults();
    };

    onDocumentKeydown = (event) => {
      if (event.key === 'Escape') {
        this.closeResults();
        this.closeDrawer();
      }
    };

    onSearchKeydown(event) {
      if (event.key === 'Escape') this.closeResults();
      if (event.key !== 'ArrowDown' || this.results?.hidden) return;
      const first = this.results.querySelector('a');
      if (first) {
        event.preventDefault();
        first.focus();
      }
    }

    async search(rawQuery) {
      const query = rawQuery.trim();
      if (query.length < 2) {
        this.results.innerHTML = '';
        this.closeResults();
        return;
      }

      this.abortController?.abort();
      this.abortController = new AbortController();
      const limit = this.dataset.searchLimit || 5;
      const url = `${this.dataset.predictiveUrl}.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,page,article&resources[limit]=${limit}&resources[options][unavailable_products]=last`;

      try {
        const response = await fetch(url, { signal: this.abortController.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
        const payload = await response.json();
        this.renderResults(payload.resources?.results || {}, query);
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.results.innerHTML = `<p class="ks-fheader__prediction-empty">Search suggestions are unavailable. Press Enter to search.</p>`;
          this.openResults();
        }
      }
    }

    renderResults(data, query) {
      const products = data.products || [];
      const secondary = [...(data.collections || []), ...(data.pages || []), ...(data.articles || [])];
      if (!products.length && !secondary.length) {
        this.results.innerHTML = `<p class="ks-fheader__prediction-empty">No suggestions found for “${this.escape(query)}”. Press Enter to view all results.</p>`;
        this.openResults();
        return;
      }

      const productMarkup = products.length ? `
        <p class="ks-fheader__predictive-heading">Products</p>
        <div class="ks-fheader__predictive-products">
          ${products.map((product) => `
            <a class="ks-fheader__prediction" href="${this.safeUrl(product.url)}" role="option">
              ${product.featured_image?.url ? `<img src="${this.imageUrl(product.featured_image.url, 124)}" alt="" width="62" height="62" loading="lazy">` : '<span></span>'}
              <span><strong>${this.escape(product.title)}</strong>${product.price ? `<small>${this.escape(product.price)}</small>` : ''}</span>
            </a>`).join('')}
        </div>` : '';

      const secondaryMarkup = secondary.length ? `
        <p class="ks-fheader__predictive-heading">Collections & pages</p>
        <div class="ks-fheader__prediction-links">${secondary.map((item) => `<a href="${this.safeUrl(item.url)}" role="option">${this.escape(item.title)}</a>`).join('')}</div>` : '';

      this.results.innerHTML = `${secondaryMarkup}${productMarkup}<div class="ks-fheader__prediction-footer"><a href="/search?q=${encodeURIComponent(query)}&options[prefix]=last">View all results →</a></div>`;
      this.openResults();
    }

    openResults() {
      if (!this.results) return;
      this.results.hidden = false;
      this.input?.setAttribute('aria-expanded', 'true');
    }

    closeResults() {
      if (!this.results) return;
      this.results.hidden = true;
      this.input?.setAttribute('aria-expanded', 'false');
    }

    openDrawer() {
      this.classList.add('is-drawer-open');
      this.drawer?.setAttribute('aria-hidden', 'false');
      this.toggle?.setAttribute('aria-expanded', 'true');
      const scrim = this.querySelector('.ks-fheader__scrim');
      if (scrim) scrim.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      this.drawer?.querySelector('button')?.focus();
    }

    closeDrawer() {
      if (!this.classList.contains('is-drawer-open')) return;
      this.classList.remove('is-drawer-open');
      this.drawer?.setAttribute('aria-hidden', 'true');
      this.toggle?.setAttribute('aria-expanded', 'false');
      const scrim = this.querySelector('.ks-fheader__scrim');
      if (scrim) scrim.hidden = true;
      document.documentElement.style.overflow = '';
    }

    refreshCart = async () => {
      if (!this.cartCount) return;
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const cart = await response.json();
        this.cartCount.textContent = cart.item_count;
        this.cartCount.classList.toggle('is-empty', cart.item_count === 0);
        this.cartCount.closest('a')?.setAttribute('aria-label', `Cart, ${cart.item_count} items`);
      } catch (_) {}
    };

    escape(value = '') {
      const element = document.createElement('div');
      element.textContent = value;
      return element.innerHTML;
    }

    safeUrl(value = '') {
      try {
        const url = new URL(value, window.location.origin);
        return ['http:', 'https:'].includes(url.protocol) ? url.href : '#';
      } catch (_) {
        return '#';
      }
    }

    imageUrl(value = '', width = 124) {
      try {
        const url = new URL(value, window.location.origin);
        if (!['http:', 'https:'].includes(url.protocol)) return '';
        url.searchParams.set('width', width);
        return url.href;
      } catch (_) {
        return '';
      }
    }
  }

  customElements.define('ks-functional-header', KSFunctionalHeader);
}
